import { useState } from 'react';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ContactModal({ open, onClose }: ContactModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      alert('문의 내용은 500자 이내로 입력해 주세요.');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      await fetch('https://readdy.ai/api/form/d7129os991ove783sni0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setCharCount(0);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 pb-5" style={{ background: 'linear-gradient(135deg, #0A508C, #2BC0D0)' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-black text-xl">상담 문의하기</div>
              <div className="text-white/70 text-xs mt-1">빠른 시일 내에 연락드리겠습니다</div>
            </div>
            <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white cursor-pointer">
              <i className="ri-close-line" />
            </button>
          </div>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: '#E6F9F0' }}>
              <i className="ri-check-line text-3xl" style={{ color: '#70C6A3' }} />
            </div>
            <div className="font-black text-gray-900 text-lg mb-2">문의가 접수되었습니다!</div>
            <div className="text-gray-500 text-sm mb-6">빠른 시일 내에 담당자가 연락드리겠습니다.</div>
            <button
              onClick={handleClose}
              className="w-full py-3 rounded-xl text-white font-bold text-sm cursor-pointer whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #0A508C, #2BC0D0)' }}
            >
              확인
            </button>
          </div>
        ) : (
          <form data-readdy-form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">이름 *</label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="홍길동"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2BC0D0] text-gray-900"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">연락처 *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder="010-0000-0000"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2BC0D0] text-gray-900"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1.5">이메일</label>
              <input
                type="email"
                name="email"
                placeholder="example@email.com"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2BC0D0] text-gray-900"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1.5">문의 유형</label>
              <select
                name="inquiry_type"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2BC0D0] text-gray-900 bg-white"
              >
                <option value="">선택해 주세요</option>
                <option value="팩 공급 문의">팩 공급 문의</option>
                <option value="라벨 디자인 문의">라벨 디자인 문의</option>
                <option value="라벨 부착 문의">라벨 부착 문의</option>
                <option value="패키지+라벨+부착 일괄">패키지+라벨+부착 일괄</option>
                <option value="브랜드/협회 패키징">브랜드/협회 패키징</option>
                <option value="기타">기타</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1.5">
                문의 내용
                <span className={`ml-2 ${charCount > 450 ? 'text-amber-500' : 'text-gray-400'}`}>{charCount}/500</span>
              </label>
              <textarea
                name="message"
                rows={4}
                maxLength={500}
                placeholder="품목, 예상 수량, 필요 서비스 등을 입력해 주세요"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2BC0D0] resize-none text-gray-900"
                onChange={(e) => setCharCount(e.target.value.length)}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 rounded-xl text-white font-bold text-sm cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #0A508C, #2BC0D0)' }}
            >
              {submitting ? '전송 중...' : '문의 접수하기'}
            </button>

            <div className="text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
              <i className="ri-phone-line" style={{ color: '#2BC0D0' }} />
              바로 통화: <a href="tel:01077023283" className="font-semibold" style={{ color: '#0A508C' }}>010-7702-3283</a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
